import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { useProducts } from '../../hooks/useProducts';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import type { Product } from '../../api/products';

const columnHelper = createColumnHelper<Product>();

export default function ProductsListPage() {
  const navigate = useNavigate();
  const { data: products, isLoading } = useProducts();

  const columns = useMemo(
    () => [
      columnHelper.accessor('productName', {
        header: 'Product Name',
        cell: (info) => (
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('upc', {
        header: 'UPC',
        cell: (info) => <span className="font-mono text-xs text-gray-600 dark:text-gray-400">{info.getValue()}</span>,
      }),
      columnHelper.accessor('serviceType', {
        header: 'Service Type',
        cell: (info) => <span className="text-sm dark:text-gray-200">{info.getValue()}</span>,
      }),
      columnHelper.accessor('productType', {
        header: 'Product Type',
        cell: (info) => <span className="text-sm dark:text-gray-200">{info.getValue()}</span>,
      }),
      columnHelper.accessor('price', {
        header: 'Price',
        cell: (info) => (
          <span className="text-sm dark:text-gray-200">${info.getValue()?.toFixed(2) || '0.00'}</span>
        ),
      }),
      columnHelper.accessor('vendorProducts', {
        header: 'Vendors',
        cell: (info) => {
          const vps = info.getValue() || [];
          const hasFc = vps.some((v) => v.vendorKey === 'first-close-one');
          return (
            <div className="flex items-center gap-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">{vps.length}</span>
              {hasFc && (
                <span className="badge bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px]">FC1</span>
              )}
            </div>
          );
        },
      }),
      columnHelper.accessor('maxEtaDays', {
        header: 'ETA (days)',
        cell: (info) => <span className="text-sm dark:text-gray-200">{info.getValue()}</span>,
      }),
    ],
    [],
  );

  return (
    <div>
      <PageHeader
        title="Products"
        description="Product catalog and vendor product mappings"
        actions={
          <button onClick={() => navigate('/products/new')} className="btn-primary gap-1">
            <Plus className="h-4 w-4" />
            New Product
          </button>
        }
      />
      <DataTable
        data={products || []}
        columns={columns}
        isLoading={isLoading}
        onRowClick={(product) => navigate(`/products/${product.uuid || product.urn}`)}
      />
    </div>
  );
}
